'use client';

import type { FormDto } from '../form.types';
import { Button, Field, Fieldset, Label } from '@headlessui/react';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import {
    FormFieldCheckbox,
    FormFieldColor,
    FormFieldDate,
    FormFieldDateTime,
    FormFieldFile,
    FormFieldHidden,
    FormFieldInput,
    FormFieldNumber,
    FormFieldRadio,
    FormFieldRange,
    FormFieldSelect,
    FormFieldTextarea,
    FormFieldTime,
} from './formfields';
import { FormSpamGuard } from './FormSpamGuard';
import { detectSpam } from '../spam-guard';
import { useFillDuration } from '../useFillDuration';
import { cn } from '@/lib/tailwind';

export function DynamicForm({ form }: { form: FormDto; className?: string }) {
    const searchParams = useSearchParams();
    const apartmentNumber = searchParams.get('apartmentNumber');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const stampFillDuration = useFillDuration();

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const formElement = e.currentTarget;
            const formData = new FormData(formElement);
            stampFillDuration(formData);

            // Honeypot filled, no JS proof or submitted too fast: show the standard
            // error and keep the values, exactly like a failed request. The route
            // checks the same signals again, since a bot can skip this code path.
            if (detectSpam(formData)) {
                setSubmitStatus('error');
                return;
            }

            const response = await fetch('/api/send-form', {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                setSubmitStatus('success');
                formElement.reset();
            } else {
                setSubmitStatus('error');
            }
        } catch {
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const hasRequiredFields = form.fields?.some((field) => field.required) ?? false;

    const getFieldWidths = () => {
        if (!form.fields) return new Map();

        const widths = new Map<string, boolean>();
        let currentRowSpan = 0;

        form.fields.forEach((field, index) => {
            if (!field._key) return;

            const isFullWidth = field.type === 'textarea';

            if (isFullWidth) {
                if (currentRowSpan === 1) {
                    const prevField = form.fields![index - 1];
                    if (prevField?._key) {
                        widths.set(prevField._key, true);
                    }
                }

                widths.set(field._key, true);
                currentRowSpan = 0;
            } else {
                currentRowSpan++;

                if (currentRowSpan === 2) {
                    currentRowSpan = 0;
                }

                widths.set(field._key, false);
            }
        });

        if (currentRowSpan === 1 && form.fields.length > 0) {
            const lastField = form.fields[form.fields.length - 1];
            if (lastField?._key) {
                widths.set(lastField._key, true);
            }
        }

        return widths;
    };

    const fieldWidths = getFieldWidths();

    return (
        <>
            {apartmentNumber && (
                <div className="mb-6 lg:mb-14 text-white text-sm lg:text-base uppercase">
                    Zur Wohnung {apartmentNumber}
                </div>
            )}
            <form id={form.id} onSubmit={handleSubmit} className="mt-6 lg:mt-14">
                {submitStatus === 'success' && (
                    <div className="mb-4 p-4 bg-green-100 text-green-700 rounded">Formular erfolgreich versendet!</div>
                )}

                {submitStatus === 'error' && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
                        Fehler beim Versenden. Bitte versuchen Sie es erneut.
                    </div>
                )}
                <Fieldset>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {form.fields?.map((field) => {
                            const fieldLabel = field.required && field.label ? `${field.label} *` : field.label;
                            const isFullWidth = fieldWidths.get(field._key!) ?? false;

                            return (
                                <Field key={field._key} className={cn('form-group', isFullWidth && 'md:col-span-2')}>
                                    {field.type !== 'hidden' && <Label className="form-label">{fieldLabel}</Label>}
                                    {field.type === 'textarea' && <FormFieldTextarea field={field} />}
                                    {field.type === 'select' && <FormFieldSelect field={field} />}
                                    {field.type === 'radio' && <FormFieldRadio field={field} />}
                                    {field.type === 'checkbox' && <FormFieldCheckbox field={field} />}
                                    {field.type === 'date' && <FormFieldDate field={field} />}
                                    {field.type === 'datetime-local' && <FormFieldDateTime field={field} />}
                                    {field.type === 'time' && <FormFieldTime field={field} />}
                                    {field.type === 'range' && <FormFieldRange field={field} />}
                                    {field.type === 'number' && <FormFieldNumber field={field} />}
                                    {field.type === 'file' && <FormFieldFile field={field} />}
                                    {field.type === 'color' && <FormFieldColor field={field} />}
                                    {field.type === 'hidden' && (
                                        <FormFieldHidden
                                            field={field}
                                            defaultValue={
                                                field.name ? (searchParams.get(field.name) ?? undefined) : undefined
                                            }
                                        />
                                    )}
                                    {(!field.type || ['text', 'email', 'tel', 'url'].includes(field.type)) && (
                                        <FormFieldInput field={field} />
                                    )}
                                </Field>
                            );
                        })}
                    </div>

                    {hasRequiredFields && (
                        <div className="mt-4 text-sm text-white">
                            *Die mit einem Stern (*) markierten Felder sind Pflichtfelder.
                        </div>
                    )}
                    {form.submitButton && (
                        <div className="mt-8">
                            <div className="flex justify-end">
                                <Button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting}>
                                    {isSubmitting ? 'Wird gesendet...' : form.submitButton.text}
                                </Button>
                            </div>
                        </div>
                    )}
                </Fieldset>
                <FormSpamGuard />
            </form>
        </>
    );
}
